import styled, { css } from "styled-components";
import { colorMixin, spacingMixin, typographyMixin } from "../../helpers";
import Block from "../Block";
import { ExpandTableCellStyle } from "./defaultColumns/expandColumn";
import { SelectTableCellStyle } from "./defaultColumns/selectColumn";
import { getTableStyles } from "./helpers";
import { TTableVariant } from "./types";
import { SortableTableCellStyle } from "./components/ColumnSorting";

type RowHeightProps = {
    $rowHeight?: string | number;
};

export const TableHead = styled.thead`
    background: var(--table-head-background);
    position: sticky;
    top: 0;
    z-index: 1;
`;
export const TableBody = styled.tbody``;

export const TableHeadCell = styled.th`
    text-align: left;
    border: none;
    color: var(--table-head-cell-color);
    padding: var(--table-head-cell-padding);
    height: 44px;
    ${typographyMixin("label-x-small-medium")};
    border-bottom: var(--table-cell-bottom-border);

    ${SortableTableCellStyle}
    ${SelectTableCellStyle}
`;

export const TableCell = styled.td<RowHeightProps>`
    ${typographyMixin("label-small-regular")};
    color: ${colorMixin("text-primary-700")};
    border: none;
    padding: var(--table-cell-padding);
    border-bottom: var(--table-cell-bottom-border);

    ${({ $rowHeight }) =>
        $rowHeight &&
        css`
            height: ${typeof $rowHeight === "number" ? `${$rowHeight * 8}px` : $rowHeight};
        `}

    ${SelectTableCellStyle}
    ${ExpandTableCellStyle}
`;

export const TableRow = styled.tr<{ $clickable?: boolean; $isOpen?: boolean }>`
    > *:first-child {
        padding-left: var(--first-and-last-column-padding, var(--table-cell-padding-x));
    }

    > *:last-child {
        padding-right: var(--first-and-last-column-padding, var(--table-cell-padding-x));
    }

    ${({ $isOpen }) =>
        $isOpen &&
        css`
            & > ${TableCell} {
                border: none;
            }
        `}

    ${({ $clickable = false }) =>
        $clickable &&
        css`
            cursor: pointer;

            :hover {
                > ${TableCell} {
                    background-color: ${colorMixin("background-primary-25")};
                }
            }
        `};
`;

export const CollapsibleRow = styled(TableRow)`
    & > ${TableCell} {
        padding-top: ${spacingMixin(0.5)};
        padding-bottom: ${spacingMixin(2)};
    }
`;

export const CollapsibleContent = styled.div`
    width: 100%;
    max-width: 100%;
    overflow: hidden;
`;

export const Table = styled.table`
    width: 100%;

    & * {
        box-sizing: border-box;
    }
`;

export const EmptyState = styled.div`
    ${typographyMixin("label-small-regular")};
    color: ${colorMixin("text-primary-700")};
    padding: ${spacingMixin(9)} 0;
    text-align: center;
`;

export const TableContainer = styled(Block)<{ $variant: TTableVariant }>`
    padding: 0;
    overflow-x: auto;

    ${({ theme, $variant, withoutContainerStyles }) => {
        const styles = getTableStyles(theme, $variant, withoutContainerStyles);

        return css`
            --table-cell-padding-x: ${styles.cellPaddingX};
            --table-cell-padding-y: ${styles.cellPaddingY};
            --table-cell-padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
            --table-cell-bottom-border: ${styles.cellBottomBorder};
            --table-head-cell-color: ${styles.headCellColor};
            --table-head-cell-padding: ${styles.headCellPadding};
            --table-head-background: ${styles.headBackground};
            --table-background: ${styles.tableBackground};
            --table-border: ${styles.tableBorder};
            --table-head-cell-sortable-color: ${styles.headCellSortableColor};
        `;
    }}

    background: var(--table-background);
    border: var(--table-border);

    & > :first-child:not(:last-child) {
        padding-top: ${spacingMixin(2)};
        padding-left: ${spacingMixin(3)};
        padding-right: ${spacingMixin(3)};
    }

    &[data-block-with-header] {
        --first-and-last-column-padding: 24px;
    }
`;

export const TableInnerContainer = styled.div<{
    $height?: number | string;
    $maxHeight?: number | string;
}>`
    width: 100%;
    overflow-y: auto;

    ${({ $height }) =>
        $height !== undefined &&
        css`
            height: ${typeof $height === "number" ? `${$height}px` : $height};
        `}

    ${({ $maxHeight }) =>
        $maxHeight !== undefined &&
        css`
            max-height: ${typeof $maxHeight === "number" ? `${$maxHeight}px` : $maxHeight};
        `}

    :last-child ${Table} ${TableBody} ${TableRow}:last-child ${TableCell} {
        border-bottom: none;
    }
`;

export const Loader = styled.div<RowHeightProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 8px 12px;
    color: ${colorMixin("text-primary-700")};

    ${({ $rowHeight }) =>
        $rowHeight &&
        css`
            min-height: ${typeof $rowHeight === "number" ? `${$rowHeight * 8}px` : $rowHeight};
        `}
`;
